import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { upperFirst } from 'lodash-es';
import { Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';

import { Company, CompanyBilling } from '@site-mate/dashpivot-shared-library';
import {
  BillingCycles,
  CompanyPlanTypes,
  IBillingPayment,
  IBillingSubscription,
  PaymentCurrencyTypes,
  PaymentMethods,
  PaymentTermContexts,
  PricingRatesDefaults,
  SubscriptionStatuses,
  SubscriptionTerms,
} from '@site-mate/sitemate-global-shared';

import { ICompanyHomeSettings } from 'app/home/company-home-settings.model';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { IntercomService } from 'app/intercom/intercom.service';
import { NavService } from 'app/layout/nav.service';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-company-billing-settings',
  templateUrl: './company-billing-settings.component.html',
  styleUrls: ['./company-billing-settings.component.scss'],
})
export class CompanyBillingSettingsComponent implements OnInit, OnDestroy {
  isSuperAdmin: boolean;
  isLoading = true;
  isSaving = false;
  company: Company;
  groupBillingTooltip: string;

  model: ICompanyHomeSettings = {
    name: '',
    id: '',
    trialExpiration: null,
    businessAttributes: null,
    jointVenture: false,
    domains: [],
    jointVentureDomains: [],
    planType: null,
    subscriptionStatus: null,
    subscriptionTerm: null,
    subscriptionInitialDate: null,
    billingCycle: null,
    paymentTermDay: null,
    paymentTermContext: null,
    paymentMethod: null,
    paymentCurrency: null,
    groupBilling: false,
    contributorsLicenses: null,
    sitematePaidUsersLicenses: null,
    contributorsLicensesRate: PricingRatesDefaults.NA,
    visitorsLicensesRate: PricingRatesDefaults.NA,
    sitemateFreeUsersLicensesRate: PricingRatesDefaults.NA,
    sitematePaidUsersLicensesRate: PricingRatesDefaults.Free,
  };

  @ViewChild('form') companyForm: NgForm;

  private unsubscribe = new Subject<void>();

  readonly planTypeSelectorValues = this.getSelectorOptions(CompanyPlanTypes);

  readonly subscriptionStatusSelectorValues = this.getSelectorOptions(SubscriptionStatuses);
  readonly subscriptionTermSelectorValues = this.getSelectorOptions(SubscriptionTerms);

  readonly paymentTermDaySelectorValues = Array.from(Array(61).keys());
  readonly paymentTermContextSelectorValues = this.getSelectorOptions(PaymentTermContexts);
  readonly paymentMethodSelectorValues = this.getSelectorOptions(PaymentMethods);
  readonly paymentCurrencySelectorValues = this.getSelectorOptions(PaymentCurrencyTypes);

  readonly billingCycleSelectorValues = this.getSelectorOptions(BillingCycles);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly errorHandler: ErrorHandler,
    private readonly toastrService: ToastrService,
    private readonly navService: NavService,
    private readonly i18nService: TmpI18NService,
    private readonly intercomService: IntercomService,
  ) {}

  ngOnInit() {
    this.setupModel();

    this.groupBillingTooltip = this.i18nService.getMessage('groupBillingTooltip');
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onToggleClicked(event: Event) {
    if (!this.isSuperAdmin) {
      event.preventDefault();
    }

    return this.isSuperAdmin;
  }

  setupModel(): void {
    this.isSuperAdmin = this.userService.isAdmin();
    this.activatedRoute.params
      .pipe(
        distinctUntilChanged(),
        switchMap((params) => this.companyService.getCompany(params.companyId)),
        takeUntil(this.unsubscribe),
      )
      .subscribe(
        (company: Company) => {
          this.company = company;
          this.model = this.companyService.getCompanyModel(company);
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          this.errorHandler.handle(error);
        },
      );
  }

  get changedFormFields(): string[] {
    const allFormFields = Object.entries(this.companyForm.controls);

    return allFormFields.filter(([, value]) => value.dirty).map(([key]) => key);
  }

  onSubmit(event: Event): void {
    event.preventDefault();

    if (!this.canSaveSettings) {
      return;
    }

    if (this.changedFormFields.length || this.hasLicensesInputsChanged) {
      this.isSaving = true;
      this.updateBilling();
    }
  }

  private resetForm(): void {
    this.companyForm.resetForm(this.companyForm.value);
  }

  updateNavAndSideBar(company: Company): void {
    this.navService.updateCompanyMetadataNav(company);
  }

  openLiveChat() {
    this.intercomService.open();
  }

  private updateBilling(): void {
    const billing = this.buildBillingObject();

    this.companyService
      .updateCompanyBilling(this.model.id, billing)
      .pipe(take(1))
      .subscribe(
        (company) => {
          this.company = company;
          this.toastrService.successByKey('companyBillingUpdated');
          this.resetForm();
          this.isSaving = false;
        },
        (error) => this.errorHandler.handle(error),
      );
  }

  private buildBillingObject(): CompanyBilling {
    const trialExpiration = this.convertToUTC(this.model.trialExpiration);
    const billing = {} as CompanyBilling;

    billing.planType = this.model.planType;
    billing.trialExpiration = new Date(trialExpiration);
    billing.groupBilling = this.model.groupBilling;

    billing.subscription = this.getSubscription();
    billing.payment = this.getBillingPayment();

    billing.licenses = {
      dashpivotContributors: this.model.contributorsLicenses,
      sitematePaidUsers: this.model.sitematePaidUsersLicenses,
    };

    billing.rates = {
      dashpivotContributorsLicensesRate: this.model.contributorsLicensesRate,
      dashpivotVisitorsLicensesRate: this.model.visitorsLicensesRate,
      sitemateFreeUsersLicensesRate: this.model.sitemateFreeUsersLicensesRate,
      sitematePaidUsersLicensesRate: this.model.sitematePaidUsersLicensesRate,
    };

    if (this.model.billingCycle) {
      billing.billingCycle = this.model.billingCycle;
    }

    return billing;
  }

  private getSubscription(): IBillingSubscription {
    const subscription = {
      status: this.model.subscriptionStatus,
      term: null,
      initialDate: null,
    };

    if (this.model.subscriptionTerm) {
      subscription.term = this.model.subscriptionTerm;
    }

    if (this.model.subscriptionInitialDate?.year) {
      const subscriptionInitialDate = this.convertToUTC(this.model.subscriptionInitialDate);

      subscription.initialDate = new Date(subscriptionInitialDate);
    }

    return subscription;
  }

  private getBillingPayment(): IBillingPayment {
    return {
      termDay: this.model.paymentTermDay ?? null,
      termContext: this.model.paymentTermContext ?? null,
      method: this.model.paymentMethod ?? null,
      currency: this.model.paymentCurrency ?? null,
    };
  }

  private convertToUTC(trialDate: NgbDate): number {
    return Date.UTC(trialDate.year, trialDate.month - 1, trialDate.day);
  }

  get canSaveSettings() {
    const hasFormChanged = this.companyForm?.form.dirty || this.hasLicensesInputsChanged;

    return this.isSuperAdmin && hasFormChanged;
  }

  get hasLicensesInputsChanged(): boolean {
    if (!this.company?.billing?.licenses || !this.model) {
      return false;
    }

    const { contributorsLicenses, sitematePaidUsersLicenses } = this.model;
    const { dashpivotContributors, sitematePaidUsers } = this.company.billing.licenses;

    const hasDashpivotUsersChanged = contributorsLicenses !== dashpivotContributors;
    const hasSitemateUsersChanged = sitematePaidUsersLicenses !== sitematePaidUsers;

    return hasDashpivotUsersChanged || hasSitemateUsersChanged;
  }

  private getSelectorOptions(options: any) {
    return Object.values(options).map((option) => {
      return { label: upperFirst(option), value: option };
    });
  }
}
